import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker";
import { FilePdf, FileXls, FileCsv, ChartBar } from "@phosphor-icons/react";
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, format,
} from "date-fns";
import { toast } from "sonner";

const PRESETS = [
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "custom", label: "Custom" },
];

const TYPE_COLORS = {
  Regular: "text-blue-400", Overtime: "text-amber-400",
  "Paid Duty": "text-emerald-400", Court: "text-purple-400", Vacation: "text-slate-400",
};

function rangeFor(preset, custom) {
  const now = new Date();
  if (preset === "week") return { start: startOfWeek(now, { weekStartsOn: 0 }), end: endOfWeek(now, { weekStartsOn: 0 }) };
  if (preset === "month") return { start: startOfMonth(now), end: endOfMonth(now) };
  if (preset === "year") return { start: startOfYear(now), end: endOfYear(now) };
  return { start: new Date(custom.start + "T00:00:00"), end: new Date(custom.end + "T00:00:00") };
}

export default function Reports() {
  const { user } = useAuth();
  const currency = user?.currency || "CAD";
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({
    start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    end: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  });
  const [exporting, setExporting] = useState("");

  const { start, end } = rangeFor(preset, custom);
  const startStr = format(start, "yyyy-MM-dd");
  const endStr = format(end, "yyyy-MM-dd");

  const { data, isLoading } = useQuery({
    queryKey: ["reports", startStr, endStr],
    queryFn: async () => (await api.get(`/reports?start=${startStr}&end=${endStr}`)).data,
  });

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/reports/export?start=${startStr}&end=${endStr}&fmt=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dutytrack_report_${startStr}_to_${endStr}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} exported.`);
    } catch {
      toast.error("Export failed.");
    }
    setExporting("");
  };

  const totals = data?.totals;

  return (
    <div className="animate-fade-up" data-testid="reports-page">
      <div className="mb-6">
        <p className="overline text-primary">Reporting</p>
        <h1 className="text-3xl font-black tracking-tight mt-1">Reports &amp; Export</h1>
        <p className="text-muted-foreground text-sm mt-1">Filter your earnings by period and export to PDF, Excel or CSV.</p>
      </div>

      <Card className="p-5 bg-card border-border mb-4">
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`preset-${p.key}`}
              onClick={() => setPreset(p.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                preset === p.key ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground hover:text-foreground"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {preset === "custom" && (
          <div className="grid grid-cols-2 gap-3 mb-4 max-w-md" data-testid="custom-range">
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">From</p>
              <DatePicker testId="report-start" value={custom.start} onChange={(d) => setCustom({ ...custom, start: d })} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">To</p>
              <DatePicker testId="report-end" value={custom.end} onChange={(d) => setCustom({ ...custom, end: d })} />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground" data-testid="report-range-label">
            {format(start, "MMM d, yyyy")} – {format(end, "MMM d, yyyy")}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="export-pdf" disabled={!!exporting} onClick={() => doExport("pdf")} className="rounded-md">
              <FilePdf className="h-4 w-4 mr-1.5 text-red-400" weight="bold" /> PDF
            </Button>
            <Button variant="outline" data-testid="export-xlsx" disabled={!!exporting} onClick={() => doExport("xlsx")} className="rounded-md">
              <FileXls className="h-4 w-4 mr-1.5 text-emerald-400" weight="bold" /> Excel
            </Button>
            <Button variant="outline" data-testid="export-csv" disabled={!!exporting} onClick={() => doExport("csv")} className="rounded-md">
              <FileCsv className="h-4 w-4 mr-1.5 text-blue-400" weight="bold" /> CSV
            </Button>
          </div>
        </div>
      </Card>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {[
            ["Regular", totals.regular, "text-blue-400"],
            ["Overtime", totals.overtime, "text-amber-400"],
            ["Paid Duty", totals.paid_duty, "text-emerald-400"],
            ["Court", totals.court, "text-purple-400"],
            ["Grand Total", totals.grand, "text-primary"],
          ].map(([label, val, cls]) => (
            <Card key={label} className="p-4 bg-card border-border" data-testid={`total-${label.toLowerCase().replace(/ /g, "-")}`}>
              <p className="overline text-muted-foreground">{label}</p>
              <p className={`text-xl font-black mt-1 ${cls}`}>{money(val, currency)}</p>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-card border-border overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-3 border-b border-border overline text-muted-foreground">
          <span className="col-span-2">Date</span><span className="col-span-2">Type</span>
          <span className="col-span-4">Description</span><span className="col-span-1">Hrs</span>
          <span className="col-span-1">Rate</span><span className="col-span-2 text-right">Amount</span>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground animate-pulse" data-testid="report-loading">Loading…</div>
        ) : !data?.rows?.length ? (
          <div className="p-12 text-center" data-testid="report-empty">
            <ChartBar className="h-10 w-10 text-muted-foreground mx-auto mb-3" weight="bold" />
            <p className="text-muted-foreground">No entries in this period.</p>
          </div>
        ) : (
          data.rows.map((r, i) => (
            <div key={i} data-testid={`report-row-${i}`}
              className="grid grid-cols-2 sm:grid-cols-12 gap-2 px-5 py-3 border-b border-border last:border-0 items-center text-sm">
              <span className="col-span-1 sm:col-span-2 text-muted-foreground">{format(new Date(r.entry_date + "T00:00:00"), "MMM d")}</span>
              <span className={`col-span-1 sm:col-span-2 font-semibold ${TYPE_COLORS[r.type] || ""}`}>{r.type}</span>
              <span className="col-span-2 sm:col-span-4 truncate">{r.description}</span>
              <span className="hidden sm:block col-span-1 text-muted-foreground">{r.hours ?? "—"}</span>
              <span className="hidden sm:block col-span-1 text-muted-foreground">{r.rate != null ? money(r.rate, currency) : "—"}</span>
              <span className="col-span-2 text-right font-bold">{r.amount != null ? money(r.amount, currency) : "—"}</span>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
